import type { ReactNode } from 'react';
import { Helmet } from 'react-helmet-async';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';

import { ConditionalWrapper } from 'atoms/layout/ConditionalWrapper';
import { FullWidthContainer } from 'atoms/layout/FullWidthContainer';
import { Vertical } from 'atoms/layout/flex';
import { BodyM, HeadlineS, LabelS } from 'atoms/typography';

const StyledVertical = styled(Vertical)`
  padding: var(--double-unit);
  background: var(--c-nd-50);
  border-radius: var(--double-unit);
`;

export type Props = {
  displayName: string;
  items: { question: string; answer: string }[];
  withContainer?: boolean;
};

export const MoreAbout = ({
  displayName,
  items,
  withContainer = true,
}: Props) => {
  return (
    <>
      <Helmet
        script={[
          {
            type: 'application/ld+json',
            innerHTML: JSON.stringify({
              '@context': 'https://schema.org',
              '@type': 'FAQPage',
              mainEntity: items.map(({ question, answer }) => ({
                '@type': 'Question',
                name: question,
                acceptedAnswer: {
                  '@type': 'Answer',
                  text: answer,
                },
              })),
            }),
          },
        ]}
      />

      <ConditionalWrapper
        wrap={withContainer}
        Wrapper={(props: { children: ReactNode }) => (
          <FullWidthContainer>
            <StyledVertical gap={1.5}>{props.children}</StyledVertical>
          </FullWidthContainer>
        )}
      >
        <HeadlineS as="h2">
          <FormattedMessage
            id="Seo.MoreAbout.moreAbout"
            defaultMessage="More about {displayName}"
            values={{ displayName }}
          />
        </HeadlineS>
        <Vertical gap={1.5}>
          {items.map(({ question, answer }, index) => (
            // eslint-disable-next-line react/no-array-index-key
            <Vertical key={index} gap={0.5}>
              <LabelS as="h3" color="var(--c-nd-600)">
                {question}
              </LabelS>
              <BodyM as="p">{answer}</BodyM>
            </Vertical>
          ))}
        </Vertical>
      </ConditionalWrapper>
    </>
  );
};
